import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bySurname } from '../lib/sort';
import { studentsAPI, placementsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, GraduationCap, Hash, ChevronRight, ChevronDown, Archive, ArchiveRestore, CheckCircle2, Clock, FileSignature, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { downloadBlob, exportTableToPdf } from '../lib/exportUtils';
import { usePagination, PaginationBar } from '../components/PaginationBar';

const StudentRecords = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [collapsedYears, setCollapsedYears] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sRes, pRes] = await Promise.all([studentsAPI.getAll(), placementsAPI.getAll()]);
      setStudents(sRes.data);
      setPlacements(pRes.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  // Build ack lookup: student_id → most recent placement ack info
  const studentAckMap = placements.reduce((acc, p) => {
    if (!acc[p.student_id] || (p.ack_status === 'signed' && acc[p.student_id].ack_status !== 'signed')) {
      acc[p.student_id] = { ack_status: p.ack_status, ack_token: p.ack_token };
    }
    return acc;
  }, {});

  const handleArchive = async (e, studentId) => {
    e.stopPropagation();
    try {
      await studentsAPI.archive(studentId);
      toast.success('Student archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive student');
    }
  };

  const handleUnarchive = async (e, studentId) => {
    e.stopPropagation();
    try {
      await studentsAPI.unarchive(studentId);
      toast.success('Student unarchived');
      fetchData();
    } catch (error) {
      toast.error('Failed to unarchive student');
    }
  };

  const toggleYear = (year) => {
    setCollapsedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const filteredStudents = students.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.student_number && s.student_number.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesYear = yearFilter === 'all' || s.year_of_study === yearFilter;
    const matchesArchive = showArchived ? s.status === 'archived' : s.status !== 'archived';
    return matchesSearch && matchesYear && matchesArchive;
  });

  const yearOrder = { '1st Year': 1, '2nd Year': 2, '3rd Year': 3 };

  const sortedStudents = filteredStudents
    .sort((a, b) => {
      const yA = yearOrder[a.year_of_study] || 99;
      const yB = yearOrder[b.year_of_study] || 99;
      if (yA !== yB) return yA - yB;
      const lA = a.course_length || '3 Year';
      const lB = b.course_length || '3 Year';
      if (lA !== lB) return lA.localeCompare(lB);
      const tA = (a.course_type || '').toLowerCase();
      const tB = (b.course_type || '').toLowerCase();
      if (tA !== tB) return tA.localeCompare(tB);
      return a.name.localeCompare(b.name);
    });
  const pg = usePagination(sortedStudents, 'student-records');
  const studentsByYear = pg.pageItems
    .reduce((acc, student) => {
      const year = student.year_of_study || '1st Year';
      const length = student.course_length || '3 Year';
      const type = student.course_type || '';
      const key = `${year}|||${length}|||${type}`;
      if (!acc[key]) acc[key] = { year, length, type, students: [] };
      acc[key].students.push(student);
      return acc;
    }, {});

  const archivedCount = students.filter(s => s.status === 'archived').length;
  const activeCount = students.filter(s => s.status !== 'archived').length;

  const handleExportExcel = async () => {
    try {
      // Only the records matching the current view (archived / active, search, filters).
      const res = await studentsAPI.exportExcel(filteredStudents.length !== students.length ? filteredStudents.map((s) => s.id) : undefined);
      downloadBlob(res.data, `student_records_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Excel export downloaded — ${filteredStudents.length} ${showArchived ? 'archived' : 'active'} student${filteredStudents.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to export student records');
    }
  };

  const handleExportPdf = async () => {
    try {
      const columns = ['Year', 'Course Length', 'Course Type', 'Student #', 'Name', 'Email', 'Status', 'Acknowledgement'];
      const rows = filteredStudents
        .slice()
        .sort((a, b) => {
          const yA = yearOrder[a.year_of_study] || 99;
          const yB = yearOrder[b.year_of_study] || 99;
          if (yA !== yB) return yA - yB;
          return bySurname(a, b);
        })
        .map((s) => {
          const ack = studentAckMap[s.id];
          const ackLabel = ack ? (ack.ack_status === 'signed' ? 'Signed' : ack.ack_status === 'pending' ? 'Pending' : '-') : '-';
          return [
            s.year_of_study || '-',
            s.course_length || '-',
            s.course_type || '-',
            s.student_number || '-',
            s.name,
            s.email,
            s.status,
            ackLabel,
          ];
        });
      await exportTableToPdf({
        title: 'Mizanplaz — Student Records',
        subtitle: `${rows.length} ${showArchived ? 'archived' : 'active'} student${rows.length === 1 ? '' : 's'}`,
        columns,
        rows,
        filename: `student_records_${new Date().toISOString().slice(0, 10)}.pdf`,
        orientation: 'landscape',
      });
      toast.success('PDF export downloaded');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      graduated: 'bg-blue-100 text-blue-700 border-blue-200',
      inactive: 'bg-gray-100 text-gray-600 border-gray-200',
      archived: 'bg-orange-100 text-orange-700 border-orange-200',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[status] || variants.active}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getYearBadge = (year) => {
    const variants = {
      '1st Year': 'bg-blue-50 text-blue-700 border-blue-200',
      '2nd Year': 'bg-purple-50 text-purple-700 border-purple-200',
      '3rd Year': 'bg-ica-gold/10 text-ica-gold-dark border-ica-gold/30',
    };
    return variants[year] || variants['1st Year'];
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="student-records-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Student Records</h1>
            <p className="text-gray-500 mt-1">View detailed student profiles and placement history</p>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="export-records-btn"
                  variant="outline"
                 
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-records-excel" onClick={handleExportExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-records-pdf" onClick={handleExportPdf}>
                  <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              data-testid="toggle-archived-btn"
              variant={showArchived ? 'default' : 'outline'}
              onClick={() => setShowArchived(!showArchived)}
              className={showArchived ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'border-gray-300'}
              size="sm"
            >
              <Archive className="w-4 h-4 mr-1.5" />
              Archived ({archivedCount})
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="search-student-records"
              placeholder="Search by name, email, or student number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger data-testid="year-filter-records" className="w-40">
              <SelectValue placeholder="Filter by Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              <SelectItem value="1st Year">1st Year</SelectItem>
              <SelectItem value="2nd Year">2nd Year</SelectItem>
              <SelectItem value="3rd Year">3rd Year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="text-sm text-gray-500">
          Showing {filteredStudents.length} {showArchived ? 'archived' : 'active'} students
        </div>

        {filteredStudents.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <GraduationCap className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500 text-center">
                {showArchived
                  ? 'No archived students found.'
                  : searchQuery || yearFilter !== 'all'
                  ? 'No students found matching your filters.'
                  : 'No students yet.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="border rounded-lg bg-white"><PaginationBar {...pg.bar} position="top" noun="students" className="border-t-0 rounded-lg" /></div>
            {Object.entries(studentsByYear).map(([key, group]) => {
              const isCollapsed = collapsedYears[key];

              return (
                <div key={key} className="border rounded-lg bg-white">
                  <button
                    data-testid={`year-group-${key}`}
                    onClick={() => toggleYear(key)}
                    className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors border-b ${getYearBadge(group.year)} bg-opacity-30`}
                  >
                    <div className="flex items-center gap-3">
                      <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                      <span className="font-semibold text-sm">{group.year}</span>
                      <span className="mx-1 text-gray-300">|</span>
                      <span className="text-sm text-gray-600">{group.length} Course</span>
                      <span className="mx-1 text-gray-300">|</span>
                      <span className="text-sm text-gray-700">{group.type || 'Unspecified Course Type'}</span>
                      <span className="text-xs text-gray-500 bg-white/60 px-2 py-0.5 rounded-full">
                        {group.students.length} student{group.students.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </button>
                  {!isCollapsed && (
                    <div className="table-scroll rounded-b-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50/50">
                          <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Year</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Course Length</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Course Type</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Student</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Student #</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Acknowledgement</th>
                          <th className="text-right py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {group.students.map((student) => (
                          <tr
                            key={student.id}
                            data-testid={`student-record-${student.id}`}
                            onClick={(e) => {
                              if (e.target.closest('button')) return;
                              navigate(`/student-records/${student.id}`);
                            }}
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getYearBadge(student.year_of_study || '1st Year')}`}>
                                {student.year_of_study || '1st Year'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-xs font-medium text-gray-600">{student.course_length || '3 Year'}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-xs font-medium text-gray-700">{student.course_type || <span className="text-gray-400 italic">—</span>}</span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-ica-navy/10 rounded-full flex items-center justify-center text-ica-navy text-sm font-semibold flex-shrink-0">
                                  {student.name.charAt(0).toUpperCase()}
                                </div>
                                <p className="font-medium text-gray-900">{student.name}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-gray-600 flex items-center gap-1">
                                <Hash className="w-4 h-4 text-gray-400" />
                                {student.student_number || 'N/A'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {getStatusBadge(student.status)}
                            </td>
                            <td className="py-3 px-4">
                              {(() => {
                                const ack = studentAckMap[student.id];
                                if (ack?.ack_status === 'signed') {
                                  return (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); if (ack.ack_token) window.open(`/placement-ack/${ack.ack_token}`, '_blank'); }}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                                      title="View signed acknowledgment"
                                    >
                                      <CheckCircle2 className="w-3 h-3" /> Signed
                                    </button>
                                  );
                                } else if (ack?.ack_status === 'pending') {
                                  return (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                      <Clock className="w-3 h-3" /> Pending
                                    </span>
                                  );
                                }
                                return <span className="text-xs text-gray-400">-</span>;
                              })()}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                {student.status === 'archived' ? (
                                  <Button
                                    data-testid={`unarchive-${student.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => handleUnarchive(e, student.id)}
                                    className="text-emerald-600 hover:text-emerald-700"
                                    title="Unarchive"
                                  >
                                    <ArchiveRestore className="w-5 h-5" />
                                  </Button>
                                ) : (
                                  <Button
                                    data-testid={`archive-${student.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => handleArchive(e, student.id)}
                                    className="text-orange-600 hover:text-orange-700"
                                    title="Archive"
                                  >
                                    <Archive className="w-5 h-5" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/student-records/${student.id}`)}
                                  className="text-gray-500"
                                >
                                  <ChevronRight className="w-5 h-5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="border rounded-lg bg-white"><PaginationBar {...pg.bar} position="bottom" noun="students" className="border-t-0 rounded-lg" /></div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentRecords;
